import { N8nModule } from '../../types';

export const module3: N8nModule = {
  id: 'zapier-integrations',
  title: 'Popular App Integrations',
  description: 'Master the most popular Zapier integrations including Gmail, Slack, Google Sheets, and more.',
  content: `# Popular App Integrations

## Google Workspace Integrations

### Gmail
Automate email workflows and management.

**Common Triggers**:
- New Email
- New Labeled Email
- New Starred Email
- New Attachment

**Common Actions**:
- Send Email
- Add Label
- Create Draft
- Mark as Read

**Use Cases**:
- Save attachments to cloud storage
- Create tasks from emails
- Auto-respond to specific emails
- Forward important messages

### Google Sheets
Use spreadsheets as databases for your automations.

**Triggers**:
- New Spreadsheet Row
- New or Updated Row
- New Worksheet

**Actions**:
- Create Spreadsheet Row
- Update Row
- Lookup Row
- Clear Rows

**Use Cases**:
- Log form submissions
- Track leads and conversions
- Generate reports automatically
- Sync data between apps

### Google Drive
Manage files and folders automatically.

**Triggers**:
- New File in Folder
- New File by Search
- New File with Label

**Actions**:
- Upload File
- Create Folder
- Move File
- Share File

## Communication Tools

### Slack
Streamline team communication and notifications.

**Triggers**:
- New Message Posted
- New Mention
- New File Shared
- Reaction Added

**Actions**:
- Send Channel Message
- Send Direct Message
- Create Channel
- Update Message

**Integration Examples**:
\`\`\`
1. Sales Alert System
   Trigger: New sale in Stripe
   → Post to #sales channel with details
   → Include customer info and amount

2. Support Ticket Notifications
   Trigger: New ticket in Zendesk
   → DM assigned team member
   → Post summary in #support

3. Error Monitoring
   Trigger: Error in app (webhook)
   → Post to #engineering channel
   → Include error details and stack trace
\`\`\`

### Microsoft Teams
Connect Microsoft's collaboration platform.

**Key Features**:
- Post messages to channels
- Send adaptive cards
- Create team notifications
- Manage channels

## CRM Integrations

### Salesforce
Automate customer relationship management.

**Triggers**:
- New Lead
- New Opportunity
- Updated Record
- New Account

**Actions**:
- Create Lead
- Update Contact
- Create Task
- Search Records

### HubSpot
Marketing and sales automation.

**Capabilities**:
- Create/update contacts
- Add to lists
- Create deals
- Log activities

**Workflow Example**:
\`\`\`
Form Submission Workflow:
1. Trigger: New form submission (Typeform)
2. Action: Create contact in HubSpot
3. Action: Add to nurture sequence
4. Action: Notify sales rep in Slack
5. Action: Log interaction
\`\`\`

## E-commerce Platforms

### Shopify
Automate your online store operations.

**Triggers**:
- New Order
- New Customer
- Order Cancelled
- Product Updated

**Actions**:
- Create Order
- Update Inventory
- Add Customer Tag
- Create Product

**Automation Ideas**:
- Send custom thank you emails
- Add customers to email lists
- Update inventory across platforms
- Alert team of high-value orders

### WooCommerce
WordPress e-commerce automation.

**Use Cases**:
- Sync orders to accounting software
- Send order notifications
- Update customer records
- Generate reports

## Project Management

### Trello
Visual project management automation.

**Triggers**:
- New Card
- Card Moved
- Label Added
- Due Date Approaching

**Actions**:
- Create Card
- Add Comment
- Move Card
- Update Card

### Asana
Task and project tracking.

**Common Workflows**:
- Create tasks from emails
- Update project status
- Assign tasks based on form responses
- Send project updates

## Marketing Tools

### Mailchimp
Email marketing automation.

**Triggers**:
- New Subscriber
- New Campaign
- Unsubscribe
- Updated Subscriber

**Actions**:
- Add/Update Subscriber
- Add to Audience
- Remove from List
- Create Campaign

### Facebook Lead Ads
Capture and process leads.

**Workflow**:
\`\`\`
Lead Processing:
1. Trigger: New lead from Facebook
2. Action: Add to CRM
3. Action: Send welcome email
4. Action: Notify sales team
5. Action: Add to Google Sheet for tracking
\`\`\`

## Best Practices for Integrations

### Data Mapping
- Map fields correctly between apps
- Use custom field mapping when needed
- Test data flow thoroughly

### Error Handling
- Set up error notifications
- Use filters to validate data
- Implement retry logic where possible

### Rate Limits
- Understand each app's limitations
- Use delays if needed
- Monitor task usage

### Security
- Use secure connections (SSL/TLS)
- Limit permissions to what's necessary
- Regularly review connected apps
- Use environment-specific accounts`,
  duration: '150 minutes',
  order: 3,
  objectives: [
    'Integrate Google Workspace apps effectively',
    'Set up communication tool automations',
    'Connect CRM and e-commerce platforms',
    'Build project management workflows',
    'Implement marketing automation'
  ],
  keyConcepts: [
    'Google Workspace integration',
    'Slack and Teams automation',
    'CRM workflows',
    'E-commerce automation',
    'Marketing tool integration'
  ]
};
